import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { PrismCard, PrismLoadingBar } from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import GeneralNavigationIntercomHelpLink from 'lumen/components/general/navigation/intercom-help-link';
import HotelAndMarketRevenueInsightLoadingStateTitle from 'lumen/components/hotel-and-market/revenue-insight/loading-state-title';
import { KPIS } from 'lumen/utils/constants/ri-constant-values';

interface HotelAndMarketRevenueInsightChartContainerSignature {
  Args: {
    kpi: string;
    // Indeterminate bar across the card top for an in-place refetch (chart kept).
    showLoadingBar?: boolean;
    showLoadingPlaceholderState: boolean;
  };
  Blocks: {
    default: [];
    // Card-header actions (e.g. pace "Chart options"); falls back to the help link.
    headerActions: [];
  };
  Element: HTMLDivElement;
}

export default class HotelAndMarketRevenueInsightChartContainer extends Component<HotelAndMarketRevenueInsightChartContainerSignature> {
  @service declare intl: Services['intl'];
  @service declare riSelections: Services['ri-selections'];

  get chartTitle(): string {
    const isNotADR = this.args.kpi !== KPIS.ADR;

    if (this.riSelections.isCancellationsOnlyView) {
      if (this.riSelections.isCancellationsPercentageView) {
        return this.intl.t('revenue_insight.pace.cancellations_percentage');
      }

      if (isNotADR) {
        return this.intl.t('revenue_insight.pace.cancellations_absolute');
      }

      return this.intl.t(
        'revenue_insight.adr_evolution.cancellations_absolute',
      );
    }
    // Behavior for pace chart here:
    if (isNotADR) {
      return this.intl.t('revenue_insight.pace');
    }

    // Separate title for pace chart block when ADR is selected on top
    return this.intl.t('revenue_insight.adr_evolution');
  }

  <template>
    <div ...attributes>
      {{#if @showLoadingBar}}
        <PrismLoadingBar data-test-id="chart-loading-bar" />
      {{/if}}
      <PrismCard>
        <:header as |Header|>
          <div class="title-box">
            {{#if @showLoadingPlaceholderState}}
              <HotelAndMarketRevenueInsightLoadingStateTitle />
            {{else}}
              <Header.title>
                {{this.chartTitle}}
              </Header.title>
            {{/if}}
          </div>
        </:header>

        <:headerAfter>
          {{#if (has-block "headerActions")}}
            {{yield to="headerActions"}}
          {{else}}
            <GeneralNavigationIntercomHelpLink
              @articleLink="/en/articles/10128857-bookings-pace-adr-evolution-graph"
              @iconName="book"
              @linkText={{t "general.learn_more"}}
              @type="text-and-icon"
              data-test-id="pace-help-link"
            />
          {{/if}}
        </:headerAfter>
        <:content>
          <div class="revenue-insight-chart-inner-container">
            {{#unless @showLoadingPlaceholderState}}
              {{yield}}
            {{/unless}}
          </div>
        </:content>
      </PrismCard>
    </div>
  </template>
}
